import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Upload, FileText, AlertCircle, X } from 'lucide-react';
import { docsApi } from '@/shared/docsApi';

export function NewDocValidationJobPage() {
  const nav = useNavigate();
  const [documents, setDocuments] = useState<File[]>([]);
  const [accountHolderName, setAccountHolderName] = useState('');

  const mutation = useMutation({
    mutationFn: docsApi.create,
    onSuccess: (data) => nav(`/docs/${data.job_id}`),
  });

  const ready = documents.length > 0;
  const totalBytes = documents.reduce((s, f) => s + f.size, 0);

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-semibold text-axis-ink mb-1">New document validation</h1>
      <p className="text-sm text-axis-ash mb-6">
        Upload identity, address, income, and nominee documents. Each is extracted with Claude Sonnet 4.5,
        then format, cross-document consistency, and fraud-signal checks run deterministically.
      </p>

      <div className="card p-6 space-y-6">
        {/* Account holder (optional) */}
        <div>
          <label className="block text-sm font-medium text-axis-ink mb-2">
            Account holder name <span className="text-axis-ash font-normal">(optional, enables self-nomination check)</span>
          </label>
          <input
            type="text"
            placeholder="e.g. RAJENDRA BHIMRAO RANE"
            className="w-full px-3 py-2 border border-axis-cloud-200 rounded-md focus:ring-2 focus:ring-axis-maroon focus:outline-none"
            value={accountHolderName}
            onChange={(e) => setAccountHolderName(e.target.value)}
          />
        </div>

        {/* Documents */}
        <div>
          <label className="block text-sm font-medium text-axis-ink mb-2">
            Documents (.pdf) — up to 20 files, 50 MB each
          </label>
          <label className="flex items-center gap-3 p-4 border-2 border-dashed border-axis-cloud-200 rounded-md hover:border-axis-maroon cursor-pointer">
            <FileText className="w-6 h-6 text-axis-maroon shrink-0" />
            <div className="flex-1">
              {documents.length > 0 ? (
                <>
                  <div className="text-sm font-medium">{documents.length} document(s) selected</div>
                  <div className="text-xs text-axis-ash">{(totalBytes / 1024 / 1024).toFixed(2)} MB total</div>
                </>
              ) : (
                <span className="text-sm text-axis-ash">
                  Click to select PDF documents (PAN, Aadhaar, passport, utility bill, nominee form, etc.)
                </span>
              )}
            </div>
            <input
              type="file"
              multiple
              accept=".pdf"
              className="hidden"
              onChange={(e) => setDocuments(Array.from(e.target.files ?? []))}
            />
          </label>

          {documents.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {documents.map((f, i) => (
                <li key={i} className="flex items-center justify-between text-sm bg-axis-cloud-50 rounded px-3 py-1.5">
                  <span className="flex items-center gap-2 truncate">
                    <FileText className="w-4 h-4 text-axis-ash shrink-0" />
                    <span className="truncate">{f.name}</span>
                    <span className="text-xs text-axis-ash shrink-0">{(f.size / 1024).toFixed(1)} KB</span>
                  </span>
                  <button
                    type="button"
                    className="text-axis-ash hover:text-axis-maroon shrink-0"
                    onClick={() => setDocuments(documents.filter((_, j) => j !== i))}
                    aria-label="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {mutation.isError && (
          <div className="flex gap-2 p-3 bg-rose-500/10 border border-rose-400/30 rounded-md text-rose-300 text-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <div>{(mutation.error as Error).message}</div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-axis-cloud-200">
          <button type="button" className="btn-secondary" onClick={() => nav('/docs')}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            disabled={!ready || mutation.isPending}
            onClick={() => {
              mutation.mutate({
                documents,
                account_holder_name: accountHolderName.trim() || undefined,
              });
            }}
          >
            <Upload className="w-4 h-4" />
            {mutation.isPending ? 'Uploading…' : 'Start validation'}
          </button>
        </div>
      </div>
    </div>
  );
}
